import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/Badge";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

export const INVENTORY_DISCREPANCY_QUEUE_PATH = "/manufacturing-management/inventory-discrepancies";

/** Queue path, optionally limited to one stock keeping unit. */
export function inventoryDiscrepancyQueuePath(sku?: string): string {
  const trimmed = sku?.trim() ?? "";
  if (trimmed === "") return INVENTORY_DISCREPANCY_QUEUE_PATH;
  const params = new URLSearchParams();
  params.set("sku", trimmed);
  return `${INVENTORY_DISCREPANCY_QUEUE_PATH}?${params.toString()}`;
}

type OpenInventoryDiscrepancySkuBadgeProps = {
  className?: string;
  /** When set, the queue opens filtered to this stock keeping unit. */
  sku?: string;
  /** When true, badge links to the manufacturing management review queue. */
  linkToQueue?: boolean;
};

/**
 * Amber badge for SKUs with open inventory discrepancy requests.
 * Parent surfaces should gate rendering with `useOpenInventoryDiscrepancySkus`.
 */
export function OpenInventoryDiscrepancySkuBadge({
  className,
  sku,
  linkToQueue = true,
}: OpenInventoryDiscrepancySkuBadgeProps): JSX.Element {
  const badge = (
    <Badge tone="warning" className={cn("shrink-0 px-1.5 py-0 text-[10px] font-semibold", className)}>
      Discrepancy
    </Badge>
  );

  const labeled = (
    <HoverHintTrigger
      align="start"
      trigger={
        <span className="inline-flex shrink-0">
          {badge}
        </span>
      }
    >
      {sku != null && sku.trim() !== ""
        ? `Open inventory discrepancies for ${sku.trim()}`
        : "Open inventory discrepancy"}
    </HoverHintTrigger>
  );

  if (!linkToQueue) {
    return labeled;
  }

  return (
    <Link
      to={inventoryDiscrepancyQueuePath(sku)}
      tabIndex={-1}
      className="inline-flex shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {labeled}
    </Link>
  );
}
